import {
  defaultPublishTiming,
  isFullSongVideoTheme,
  type CreateAssetPublishBody,
} from 'fym-shared/src/music24/oneOffPublish';
import { localReleaseStart, publishAtOfValue, type PublishTimingValue } from './PublishTimingField';

/** The Studio switch "Publish to YouTube when ready" and its choices. */
export interface StudioPublishChoice {
  enabled: boolean;
  timing: PublishTimingValue;
  /** No default (YouTube terms): the request waits for the answer. */
  madeForKids: boolean | null;
}

export function initialStudioPublishChoice(
  releaseDate: string | null,
  now: Date = new Date(),
  timeZone: string | null = null
): StudioPublishChoice {
  return {
    enabled: false,
    timing: { timing: defaultPublishTiming('youtube', localReleaseStart(releaseDate, timeZone), now), pickedAt: '' },
    madeForKids: null,
  };
}

/**
 * The publish request that "Make it" sends for a made asset, or null when the switch is off, the
 * asset is not a full-song video, or a choice is missing ("Make it" then stays off: see
 * `studioPublishBlocksMake`).
 */
export function studioPublishBody(
  choice: StudioPublishChoice,
  asset: { uuid: string; themeId: string },
  releaseDate: string | null,
  timeZone: string | null = null
): CreateAssetPublishBody | null {
  if (!choice.enabled || !isFullSongVideoTheme(asset.themeId) || choice.madeForKids === null) {
    return null;
  }
  const publishAt = publishAtOfValue(choice.timing, releaseDate, timeZone);
  if (choice.timing.timing === 'later' && publishAt === null) {
    return null;
  }
  return {
    kitAssetUuid: asset.uuid,
    channel: 'youtube',
    timing: choice.timing.timing,
    publishAt,
    madeForKids: choice.madeForKids,
  };
}

/** True when the switch is on but a choice is missing: "Make it" waits for it. */
export function studioPublishBlocksMake(
  choice: StudioPublishChoice,
  releaseDate: string | null,
  timeZone: string | null = null
): boolean {
  if (!choice.enabled) {
    return false;
  }
  return studioPublishBody(choice, { uuid: '-', themeId: 'lyricVideo' }, releaseDate, timeZone) === null;
}
